import { useEffect, useState } from 'react';
import { Smartphone, Download, X } from 'lucide-react';
import api from '@/lib/api';
import { isCapacitorNative } from '@/lib/platformDetect';

const DISMISS_KEY = 'apk_update_dismissed_sha';
const CHECK_EVERY_MS = 30 * 60 * 1000;
// iter953 — always download from the PUBLIC APK endpoint (never GitHub).
const APK_DOWNLOAD_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/api/downloads/apk/latest`;

/** Read the installed APK versionCode via @capacitor/app (Android `build`).
 *  Returns null on web or when the native App plugin isn't packaged yet. */
async function getInstalledBuild() {
  if (!isCapacitorNative()) return null;
  try {
    const mod = await import('@capacitor/app');
    const info = await mod.App.getInfo();        // { version, build }
    const b = parseInt(info?.build, 10);
    return Number.isFinite(b) ? b : null;
  } catch {
    return null; // older APK without the plugin → DEVICE-PENDING until rebuilt
  }
}

/**
 * NATIVE-ONLY APK update banner. Shows ONLY inside the Android app and ONLY
 * when the installed APK versionCode is lower than the latest approved APK
 * versionCode (CI run_number). A normal WEB deployment NEVER triggers this —
 * web freshness is handled separately by DeploymentFreshnessBanner + the
 * service worker. This removes the old false positive where any web/git
 * commit change prompted an "APK update".
 */
export const ApkUpdateBanner = () => {
  const [info, setInfo] = useState(null);
  const [apkMeta, setApkMeta] = useState(null); // iter954 — version + size for the popup

  useEffect(() => {
    if (!info) return;
    api.get('/downloads/apk/info')
      .then(({ data }) => { if (data?.exists) setApkMeta(data); })
      .catch(() => {});
  }, [info]);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        // Native app only — web devices update via the service worker.
        if (!isCapacitorNative()) return;
        if (!localStorage.getItem('token') && !localStorage.getItem('cashier_token')) return;

        const installedBuild = await getInstalledBuild();
        // No readable versionCode (APK not yet rebuilt with @capacitor/app) →
        // stay silent rather than risk a false prompt. DEVICE-PENDING.
        if (installedBuild == null) return;

        const v = await fetch('/version.json', { cache: 'no-store' }).then((r) => r.json()).catch(() => null);
        const current = v?.commit && v.commit !== 'nogit' ? v.commit : '';

        // versionCode comparison happens server-side in /app-update/check.
        let found = null;
        if (localStorage.getItem('token')) {
          try {
            const { data } = await api.get('/app-update/check', { params: { current, build: installedBuild } });
            if (data?.update_available) {
              found = { ...data, latest_sha: data.latest_sha || String(data.latest_build || ''), apk_url: APK_DOWNLOAD_URL };
            }
          } catch { /* not authorised / offline — ignore */ }
        }

        if (!alive) return;
        const dismissKey = found ? String(found.latest_build || found.latest_sha) : '';
        if (found && localStorage.getItem(DISMISS_KEY) !== dismissKey) {
          setInfo(found);
        } else {
          setInfo(null);
        }
      } catch { /* silent — banner is best-effort */ }
    };
    check();
    const t = setInterval(check, CHECK_EVERY_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!info) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(info.latest_build || info.latest_sha));
    setInfo(null);
  };

  // iter953 — direct download + install: navigating straight to the APK URL
  // (served with the Android package MIME type) makes the device download and
  // launch the installer, rather than opening a new tab to a web page.
  const startUpdate = (e) => {
    if (e) e.preventDefault();
    const url = APK_DOWNLOAD_URL;
    try { window.location.assign(url); } catch { window.location.href = url; }
  };

  return (
    <div
      data-testid="apk-update-banner"
      role="status"
      className="fixed top-0 inset-x-0 z-[59] flex items-center justify-center gap-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm py-2 px-4 shadow-lg"
    >
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
      </span>
      <Smartphone size={15} className="shrink-0" />
      <span className="font-semibold">
        Smarto ERP System Update{info.build_number ? ` (#${info.build_number})` : ''} available.
      </span>
      {(info.build_number || apkMeta?.version || apkMeta?.size_mb) && (
        <span
          data-testid="apk-update-meta"
          className="hidden sm:inline text-[11px] font-medium text-white/85 bg-white/15 rounded px-2 py-0.5"
        >
          v{info.build_number || apkMeta?.version}{apkMeta?.size_mb ? ` | ${apkMeta.size_mb} MB` : ''}
        </span>
      )}
      <span
        data-testid="apk-update-whatsnew"
        className="hidden md:inline text-[11px] text-white/85"
      >
        What's New: Supply Chain Upgrades &amp; Performance Fixes
      </span>
      <a
        href={APK_DOWNLOAD_URL}
        onClick={startUpdate}
        download
        data-testid="apk-update-download-btn"
        className="flex items-center gap-1.5 rounded-md bg-white text-emerald-700 font-bold px-3 py-1 text-xs hover:bg-emerald-50 transition"
      >
        <Download size={12} /> Update Now
      </a>
      <button
        type="button"
        onClick={dismiss}
        data-testid="apk-update-dismiss-btn"
        className="text-xs underline opacity-90 hover:opacity-100 flex items-center gap-1"
      >
        <X size={12} /> Later
      </button>
    </div>
  );
};

export default ApkUpdateBanner;
